import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMapEvents } from "react-leaflet";
import FunctionalButton from "components/layout/FunctionalButton";
import { point2DToLatLng } from "lib/utils/geometry";

const MARKER_GAP_PX = 32;
const EDGE_PAD_PX = 8;

/**
 * Map click / marker context menu. Prefers opening above the point; flips below
 * (and clamps in-bounds) when near the map edge so the list is not clipped.
 */
const PointActionMenu = ({ visible, position, options, onClose }) => {
    const menuRef = useRef(null);
    const [placement, setPlacement] = useState(null);

    const map = useMapEvents({});

    // Seed anchor from map coordinates; refined after measure.
    useEffect(() => {
        if (!visible || !position || !map) {
            setPlacement(null);
            return;
        }

        const latLng = point2DToLatLng(position);
        const point = map.latLngToContainerPoint(latLng);
        setPlacement({
            x: point.x,
            y: point.y,
            placeAbove: true,
        });
    }, [visible, position, map, options?.length]);

    // After paint, flip / clamp so the menu stays inside the map container.
    useLayoutEffect(() => {
        if (!visible || !placement || !map || !menuRef.current) return;

        const size = map.getSize();
        const menu = menuRef.current;
        const menuW = menu.offsetWidth;
        const menuH = menu.offsetHeight;
        const anchor = map.latLngToContainerPoint(point2DToLatLng(position));

        const spaceAbove = anchor.y - MARKER_GAP_PX - EDGE_PAD_PX;
        const spaceBelow = size.y - anchor.y - MARKER_GAP_PX - EDGE_PAD_PX;
        let placeAbove = true;
        if (menuH > spaceAbove && spaceBelow >= spaceAbove) {
            placeAbove = false;
        }

        let x = anchor.x;
        const halfW = menuW / 2;
        x = Math.min(size.x - EDGE_PAD_PX - halfW, Math.max(EDGE_PAD_PX + halfW, x));

        let y;
        if (placeAbove) {
            y = anchor.y - MARKER_GAP_PX;
            // If still overflowing the top after choosing "above", pin below the top edge.
            if (y - menuH < EDGE_PAD_PX) {
                y = EDGE_PAD_PX + menuH;
            }
        } else {
            y = anchor.y + MARKER_GAP_PX;
            if (y + menuH > size.y - EDGE_PAD_PX) {
                y = Math.max(EDGE_PAD_PX, size.y - EDGE_PAD_PX - menuH);
                // When clamped to bottom, treat as top-left style (no -100% translate).
                placeAbove = false;
            }
        }

        if (
            placement.x !== x ||
            placement.y !== y ||
            placement.placeAbove !== placeAbove
        ) {
            setPlacement({ x, y, placeAbove });
        }
    }, [visible, placement, map, position, options?.length]);

    useEffect(() => {
        if (!visible) return;

        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [visible, onClose]);

    if (!visible || !placement) return null;

    const style = {
        position: "absolute",
        left: `${placement.x}px`,
        top: `${placement.y}px`,
        transform: placement.placeAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        zIndex: 1000,
        pointerEvents: "auto",
    };

    const coordLabel =
        position &&
        Number.isFinite(Number(position.x)) &&
        Number.isFinite(Number(position.y))
            ? `X ${Number(position.x).toFixed(3)}  Y ${Number(position.y).toFixed(3)}`
            : null;

    return (
        <div
            ref={menuRef}
            style={style}
            className="flex flex-col space-y-2 rounded-r5 bg-black bg-opacity-70 p-2"
        >
            {coordLabel ? (
                <p className="px-1 py-0.5 text-center font-mono text-[11px] tabular-nums text-white/90">
                    {coordLabel}
                </p>
            ) : null}
            {options.map((option, index) => (
                <FunctionalButton
                    key={index}
                    title={option.label}
                    onClick={option.onClick}
                />
            ))}
        </div>
    );
};

export default PointActionMenu;
